import { CheckCircle2, AlertTriangle, Loader2, XCircle, FileText, Shield } from "lucide-react";
import ParserDiagnostics from "./ParserDiagnostics";

const STATE_UI = {
  idle: {
    title: "No active batch yet",
    copy: "Upload financial files to start a new working batch.",
    colour: "border-slate-800/30 bg-slate-900/20 text-slate-500",
    Icon: Shield,
  },
  uploading: {
    title: "Uploading files…",
    copy: "Your files are being received and prepared for intake.",
    colour: "border-cyan-500/20 bg-cyan-500/[0.04] text-cyan-400",
    Icon: Loader2,
    spinning: true,
  },
  processing: {
    title: "Processing uploaded files",
    copy: "The Document Intake Engine is extracting and normalizing financial records from your files.",
    colour: "border-cyan-500/20 bg-cyan-500/[0.04] text-cyan-400",
    Icon: Loader2,
    spinning: true,
  },
  completed: {
    title: "Document Intake Complete",
    copy: "Your uploaded files have been converted into structured records for financial diagnosis.",
    colour: "border-emerald-500/20 bg-emerald-500/[0.04] text-emerald-400",
    Icon: CheckCircle2,
  },
  completed_with_issues: {
    title: "Document Intake Completed with Issues",
    copy: "Some files or records require review, but valid records have been prepared for financial diagnosis.",
    colour: "border-amber-500/20 bg-amber-500/[0.04] text-amber-400",
    Icon: AlertTriangle,
  },
  review_required: {
    title: "Routed to Review",
    copy:
      "Your upload landed safely but the auto-pipeline did not produce ledger-safe records. " +
      "See the per-file reason below for the exact cause.",
    colour: "border-amber-500/20 bg-amber-500/[0.04] text-amber-400",
    Icon: AlertTriangle,
  },
  failed: {
    title: "Document Intake Failed",
    copy: "No usable records could be prepared from the uploaded files. Please review the failed file details.",
    colour: "border-red-500/20 bg-red-500/[0.04] text-red-400",
    Icon: XCircle,
  },
};

/**
 * @param {{
 *   intakeState: string,
 *   filesAttempted: number,
 *   filesSucceeded: number,
 *   filesFailed: number,
 *   recordsExtracted: number,
 *   recordsNormalized: number,
 *   recordsFlaggedForReview: number,
 * }} props
 */
export default function IntakeSummaryPanel({
  intakeState, filesAttempted, filesSucceeded, filesFailed,
  recordsExtracted, recordsNormalized, recordsFlaggedForReview,
  pipelineMessage = "",
  fileErrors = [],
}) {
  if (!intakeState || intakeState === "idle") return null;
  const ui = STATE_UI[intakeState] || STATE_UI.processing;
  const Icon = ui.Icon;
  const stats = [
    { label: "Files attempted",  value: filesAttempted, testid: "intake-files-attempted" },
    { label: "Files succeeded",  value: filesSucceeded, testid: "intake-files-succeeded" },
    { label: "Files failed",     value: filesFailed, testid: "intake-files-failed" },
    { label: "Records extracted", value: recordsExtracted, testid: "intake-records-extracted" },
    { label: "Records normalized", value: recordsNormalized, testid: "intake-records-normalized" },
    { label: "Flagged for review", value: recordsFlaggedForReview, testid: "intake-records-flagged" },
  ];
  return (
    <div className={`rounded-xl border ${ui.colour} p-5`} data-testid="intake-summary-panel" data-intake-state={intakeState}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-800/60 border border-slate-700/30 flex items-center justify-center shrink-0">
          <Icon className={`w-5 h-5 ${ui.spinning ? "animate-spin" : ""}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-barlow font-bold" data-testid="intake-summary-title">{ui.title}</h3>
          <p className="text-xs text-slate-400 mt-1">{ui.copy}</p>
          {/* Pipeline-level message (review_required / failed) — the
              actual reason the backend returned. Shown verbatim so
              users can see "No bank statements detected ..." rather
              than a generic spinner. */}
          {pipelineMessage && (intakeState === "review_required" || intakeState === "failed") && (
            <p className="text-xs text-amber-200/90 mt-2 leading-relaxed"
               data-testid="intake-pipeline-message">
              {pipelineMessage}
            </p>
          )}
          {/* Per-file error breakdown — populated by /diagnostics. */}
          {fileErrors && fileErrors.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="intake-file-errors">
              {fileErrors.map((e, i) => (
                <li key={`${e.file_id || i}`} className="text-xs bg-slate-900/40 rounded-md p-2 border border-amber-500/15"
                    data-testid={`intake-file-error-${e.file_id || i}`}>
                  <div className="font-mono text-amber-300/90">{e.filename || "(unknown file)"}</div>
                  <div className="text-slate-300 mt-1">{e.user_message || e.error_reason}</div>
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">
                    {e.error_code} · {e.document_type_detected || "unknown"}{" "}
                    ({Math.round((e.document_type_confidence || 0) * 100)}% confidence)
                  </div>
                </li>
              ))}
            </ul>
          )}
          {(intakeState === "completed" || intakeState === "completed_with_issues") && (
            <>
              <p className="text-[10px] text-slate-500 mt-2 italic">
                Only normalized records are passed into the AI Diagnostic Engine. Raw uploaded files are not diagnosed directly.
              </p>
              <p className="text-[10px] text-slate-500 mt-1 italic" data-testid="pdf-engine-trust-copy">
                PDF bank statements are first passed through the PDF Statement Engine, which extracts text, detects statement structure, identifies transaction-like rows, and converts them into normalized records before AI diagnosis.
              </p>
            </>
          )}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
        {stats.map(s => (
          <div key={s.label} className="px-3 py-2 rounded-md bg-slate-800/30" data-testid={s.testid}>
            <div className="text-[9px] text-slate-500 uppercase tracking-wider">{s.label}</div>
            <div className="font-mono font-bold text-base text-slate-100 mt-0.5">{s.value ?? 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {{ files: any[], onRetryFile?: (id: string) => void, onRemoveFile?: (id: string) => void }} props
 */
export function FileResultsList({ files, onRetryFile, onRemoveFile }) {
  if (!files || files.length === 0) return null;
  const statusStyle = (f) => {
    const isPdf = f.file_type === "pdf";
    switch (f.status) {
      case "parsed":  return {
        bg: "bg-emerald-500/15", text: "text-emerald-400",
        label: isPdf ? "Parsed successfully" : "Parsed successfully",
        hint: isPdf ? `${f.records_extracted} transactions extracted and normalized` : null,
      };
      case "partial": return {
        bg: "bg-amber-500/15", text: "text-amber-400",
        label: isPdf ? "Processed with review required" : "Partially processed",
        hint: isPdf ? "Fallback reviewable record created from weakly structured PDF text" : null,
      };
      case "failed":  return {
        bg: "bg-red-500/15", text: "text-red-400",
        label: isPdf ? "Could not extract readable statement text" : "Failed to parse",
        hint: isPdf ? "This file needs retry or manual review" : null,
      };
      case "pooled":  return { bg: "bg-cyan-500/15", text: "text-cyan-400", label: "Pooled", hint: null };
      default:        return { bg: "bg-slate-500/15", text: "text-slate-400", label: f.status, hint: null };
    }
  };
  return (
    <div className="space-y-2" data-testid="file-results-list">
      {files.map((f) => {
        const st = statusStyle(f);
        // P0 Feb-2026 — the upload-time path emits `id`, while the
        // /status polling/rehydrate path emits `file_id`. Tolerate
        // both so every per-file data-testid carries a real UUID.
        const fid = f.file_id || f.id;
        return (
          <div key={fid} className="flex items-center gap-4 px-4 py-3 rounded-lg bg-slate-900/30 border border-slate-800/40"
            data-testid={`file-result-row-${fid}`}>
            <div className="w-8 h-8 rounded-md bg-slate-800/60 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-200 font-medium truncate" data-testid={`file-result-name-${fid}`}>{f.filename}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.bg} ${st.text} font-medium`} data-testid={`file-result-status-${fid}`}>
                  {st.label}
                </span>
                {f.document_type && f.document_type !== "unknown" && (
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider" data-testid={`file-result-doctype-${fid}`}>
                    · {f.document_type.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500 font-mono">
                <span>extracted: <span className="text-slate-300">{f.records_extracted}</span></span>
                <span>normalized: <span className="text-cyan-400">{f.records_normalized}</span></span>
                {f.records_flagged_for_review > 0 && <span>review: <span className="text-amber-400">{f.records_flagged_for_review}</span></span>}
                {f.file_type && <span className="text-slate-600">· {f.file_type}</span>}
              </div>
              {f.error && (
                <p className="text-xs text-red-400/80 mt-1" data-testid={`file-result-error-${fid}`}>{f.error}</p>
              )}
              {st.hint && (
                <p className="text-[10px] text-slate-400/80 mt-1 italic" data-testid={`file-result-hint-${fid}`}>{st.hint}</p>
              )}
              {/* P0 Feb-2026 — Parser cascade visibility */}
              <ParserDiagnostics
                diagnostics={f.parser_diagnostics}
                fileId={fid}
              />
            </div>
            {f.status === "failed" && onRetryFile && (
              <button className="text-[10px] text-cyan-400 hover:text-cyan-300 shrink-0" onClick={() => onRetryFile(fid)}
                data-testid={`file-retry-btn-${fid}`}>Retry</button>
            )}
          </div>
        );
      })}
    </div>
  );
}
